import { ArrowUpRight } from 'lucide-react';
import { Badge } from '../../components/ui/Badge';
import { Card, CardHeader } from '../../components/ui/Card';
import { Table, THead, Th, Td, Tr, isConstant } from '../../components/ui/Table';
import { Pagination, usePagination } from '../../components/ui/Pagination';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/States';
import { formatDayMonthTime } from '../../utils/dateFormat';
import { useLinkedInLeads } from '../../hooks/useJourneys';
import type { LinkedInLeadRow, ListPeriod } from '../../api/web-analytics';
import { fmtNumber } from '../Dashboard/gtm/components/format';

/** Leads per form, most first, for the line above the table. */
function formCounts(rows: LinkedInLeadRow[]): Array<[string, number]> {
  const counts = new Map<string, number>();
  for (const r of rows) {
    const form = r.formName ?? 'Unknown form';
    counts.set(form, (counts.get(form) ?? 0) + 1);
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1]);
}

const NO_ROWS: LinkedInLeadRow[] = [];

/**
 * One Attio status style: a lead that reached Attio gets the row-end link out
 * (the healthy case shows nothing else); every other state is a badge, red
 * only when the lead is missing from Attio.
 */
function AttioCell({ row }: { row: LinkedInLeadRow }) {
  switch (row.attioStatus) {
    case 'added':
      return row.attioUrl ? (
        <a
          href={row.attioUrl}
          target="_blank"
          rel="noreferrer"
          aria-label="Open in Attio"
          title="Open in Attio"
          className="inline-flex h-7 w-7 items-center justify-center rounded text-text-tertiary hover:bg-surface-secondary hover:text-text-primary"
        >
          <ArrowUpRight className="h-4 w-4" aria-hidden />
        </a>
      ) : (
        <Badge>In Attio, no link</Badge>
      );
    case 'notion':
      return <Badge>Via Notion sync</Badge>;
    case 'no_email':
      return <Badge>No email</Badge>;
    case 'pending':
      return <Badge variant="error">Not in Attio</Badge>;
  }
}

/**
 * Marketing → Website: everyone who filled in a LinkedIn lead gen form. They
 * reach the OS and Attio within seconds through LinkedIn's Lead Sync webhook;
 * a daily catch-up fetches any the webhook missed.
 */
export function LinkedInLeadsCard({ period, label }: { period: ListPeriod; label?: string }) {
  const { data, isLoading, error, refetch } = useLinkedInLeads(period);
  const rows = data?.leads ?? NO_ROWS;
  const pager = usePagination(rows, 15);
  const pending = rows.filter((r) => r.attioStatus === 'pending').length;
  // One form on every row: say it once above the table instead of a column of repeats.
  const oneForm = rows.length > 1 && isConstant(rows, (r) => r.formName ?? '');

  return (
    <Card>
      <CardHeader
        title="LinkedIn lead forms"
        description={
          data
            ? `${label ? `${label} · ` : ''}${fmtNumber(rows.length)} ${rows.length === 1 ? 'lead' : 'leads'}, newest first. Each one lands in Attio on “[INB] LinkedIn Lead Forms” within seconds; webinar forms also go to that webinar’s registrations list.`
            : undefined
        }
      />
      {isLoading ? (
        <LoadingState rows={3} />
      ) : error ? (
        <ErrorState title="Couldn’t load LinkedIn leads" error={error} onRetry={() => refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState
          compact
          title="No LinkedIn leads in this period"
          description="They arrive from LinkedIn's Lead Sync webhook as forms are filled in."
        />
      ) : (
        <div className="space-y-3">
          {pending > 0 && (
            <ErrorState
              compact
              title={`${pending} ${pending === 1 ? 'lead hasn’t' : 'leads haven’t'} reached Attio`}
              error="The webhook couldn’t add them. The morning sync retries; check the API logs for linkedin-ads/leads if it persists."
            />
          )}
          {/* A plain line, not a bordered box: a box inside the card doubled the borders. */}
          <p className="text-body-xs text-text-tertiary">
            {oneForm ? 'All from ' : 'By form: '}
            {formCounts(rows)
              .map(([form, n]) => (oneForm ? form : `${form} ${fmtNumber(n)}`))
              .join(' · ')}
          </p>
          <div>
            <Table>
              <THead>
                <tr>
                  <Th>Submitted</Th>
                  <Th>Person</Th>
                  {!oneForm && <Th>Form</Th>}
                  <Th align="right">Attio</Th>
                </tr>
              </THead>
              <tbody>
                {pager.pageItems.map((r) => (
                  <Tr key={r.id} className="align-top">
                    <Td className="text-text-secondary tabular-nums whitespace-nowrap pr-4">
                      {formatDayMonthTime(r.submittedAt)}
                    </Td>
                    <Td>
                      <div className="text-text-primary">{r.name ?? '—'}</div>
                      {r.email && <div className="break-all text-body-xs text-text-tertiary">{r.email}</div>}
                      {oneForm && r.event && (
                        <div className="mt-1">
                          <Badge>{r.event} registration</Badge>
                        </div>
                      )}
                    </Td>
                    {!oneForm && (
                      <Td className="break-words text-text-secondary">
                        {r.formName ?? 'Unknown form'}
                        {r.event && (
                          <div className="mt-1">
                            <Badge>{r.event} registration</Badge>
                          </div>
                        )}
                      </Td>
                    )}
                    <Td align="right">
                      <AttioCell row={r} />
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination {...pager} onPage={pager.setPage} />
          </div>
        </div>
      )}
    </Card>
  );
}
